/**
 * Authenticated fetch for a NAMED organization — the body of `useAuthFetch`,
 * without `useRole()`. For a provider that sits below the organization context
 * but above the role adapter: PermissionContext reads `/access/me` (#1645) for
 * the selected organization, and `useRole()` there would read a context that
 * is not mounted yet.
 */
import { useStableGetToken } from '@/hooks/useClerkGate';
import { authenticatedFetch, type AuthenticatedFetchOptions } from '@/lib/fetch-utils';
import { useCallback } from 'react';

export interface AuthData {
  token: string;
  selectedOrgId: string;
}

export function useOrgAuthFetch(selectedOrgId: string | null) {
  // Non-reactive token access: Clerk's useAuth() re-renders every consumer
  // on each ~50s token refresh — with useApiClient used across the app this
  // was an app-wide render storm (see useClerkGate.ts).
  const getToken = useStableGetToken();

  /**
   * Get authentication data (token and orgId)
   * Throws if either is missing or still loading
   */
  const getAuthData = useCallback(async (): Promise<AuthData> => {
    // Validate orgId BEFORE async operation to fail fast
    // Note: We don't check isLoading here to avoid recreating this function
    // on every loading state change (prevents infinite API call loops)
    if (!selectedOrgId) {
      throw new Error('Organization not selected');
    }

    // Capture current orgId before async call (prevents stale closure)
    const currentOrgId = selectedOrgId;

    const token = await getToken();
    if (!token) {
      throw new Error('Failed to get authentication token');
    }

    // Re-validate orgId hasn't changed during async operation
    if (!selectedOrgId || selectedOrgId !== currentOrgId) {
      throw new Error('Organization changed during authentication');
    }

    return { token, selectedOrgId: currentOrgId };
  }, [getToken, selectedOrgId]);

  /**
   * Authenticated fetch wrapper - automatically adds token and orgId
   */
  const fetchWithAuth = useCallback(
    async <T = unknown>(
      url: string,
      options?: AuthenticatedFetchOptions,
      maxRetries = 3
    ): Promise<T> => {
      const { token, selectedOrgId } = await getAuthData();
      return authenticatedFetch<T>(
        url,
        {
          value: token,
          // Clerk rotates the JWT about every minute, so a request begun just
          // before expiry can arrive just after it. `getAuthData` asks Clerk
          // again; when the old token really has expired Clerk's own cache has
          // too and it mints a new one. If it hands back the same string
          // nothing would differ and the repeat is skipped.
          refresh: async () => (await getAuthData()).token,
        },
        selectedOrgId,
        options,
        maxRetries
      );
    },
    [getAuthData]
  );

  return {
    getAuthData,
    authenticatedFetch: fetchWithAuth,
  };
}
